import React, { useCallback, useRef, useState, useMemo } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Container,
  Col,
} from "reactstrap";
import { Link, useHistory, useLocation } from "react-router-dom";
import NotificationAlert from "react-notification-alert";

import { Form } from "@unform/web";
import * as Yup from "yup";
import logo from "../../../assets/img/autopilot-logo.png";
import getValidationErrors from "../../../utils/getValidationErrors";
import CustomInputGroupText from "components/CustomInputGroupText";
import { useToast } from "../../../hooks/toast";
import api from "../../../services/api";
import "../Login/login.css";

// PO1-2000 — confirmação do reset.
// Recebe ?token=<jwt> via URL, valida política de senha (espelhando o backend
// PasswordPolicy: 8+ chars, 3 das 4 classes), submete em POST
// /auth/password-reset/confirm. Em sucesso redireciona pro login.

function classesPresentes(senha) {
  let n = 0;
  if (/[a-z]/.test(senha)) n += 1;
  if (/[A-Z]/.test(senha)) n += 1;
  if (/[0-9]/.test(senha)) n += 1;
  if (/[^A-Za-z0-9]/.test(senha)) n += 1;
  return n;
}

const ResetPassword = () => {
  const formRef = useRef(null);
  const alertRef = useRef();
  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);

  const token = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("token");
  }, [location.search]);

  const handleSubmit = useCallback(
    async (data) => {
      try {
        formRef.current.setErrors({});

        const schema = Yup.object().shape({
          novaSenha: Yup.string()
            .required("Senha é obrigatória")
            .min(8, "A senha deve ter pelo menos 8 caracteres")
            .test(
              "complexidade",
              "Use pelo menos 3 dos 4: minúscula, maiúscula, número, símbolo",
              (v) => classesPresentes(v || "") >= 3
            ),
          confirmacao: Yup.string()
            .required("Confirme a nova senha")
            .oneOf([Yup.ref("novaSenha")], "As senhas não coincidem"),
        });
        await schema.validate(data, { abortEarly: false });

        if (!token) {
          addToast({
            place: "tr",
            type: "danger",
            title: "Token ausente",
            description:
              "Link de redefinição inválido. Solicite um novo e-mail.",
            icon: "tim-icons icon-alert-circle-exc",
          });
          return;
        }

        setSubmitting(true);
        await api.post("/auth/password-reset/confirm", {
          token,
          novaSenha: data.novaSenha,
        });
        addToast({
          place: "tr",
          type: "success",
          title: "Sucesso",
          description: "Senha redefinida. Faça login com a nova senha.",
          icon: "tim-icons icon-check-2",
        });
        history.push("/auth/login");
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          formRef.current.setErrors(getValidationErrors(error));
          return;
        }
        const apiMessage =
          (error.response &&
            error.response.data &&
            (error.response.data.message || error.response.data.error)) ||
          "Não foi possível redefinir a senha. Solicite um novo link.";
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro",
          description:
            typeof apiMessage === "string"
              ? apiMessage
              : "Erro ao redefinir senha.",
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setSubmitting(false);
      }
    },
    [addToast, history, token]
  );

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={alertRef} />
      </div>
      <div className="content">
        <Container>
          <Col className="ml-auto mr-auto" lg="4" md="6">
            <Form ref={formRef} onSubmit={handleSubmit}>
              <Card className="glass-card">
                <CardHeader>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(255, 255, 255, 0.92)",
                      borderRadius: "16px",
                      width: "160px",
                      height: "80px",
                      marginBottom: "16px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
                    }}
                  >
                    <img
                      alt="Everlog"
                      src={logo}
                      style={{ width: "140px", objectFit: "contain" }}
                    />
                  </div>
                  <h4>Definir nova senha</h4>
                </CardHeader>
                <CardBody>
                  {!token && (
                    <p style={{ color: "#ffb3b3", marginBottom: 16 }}>
                      Token ausente na URL. Acesse o link recebido por e-mail.
                    </p>
                  )}
                  <p style={{ marginBottom: 16, fontSize: 13 }}>
                    A senha deve ter no mínimo 8 caracteres e conter pelo menos
                    3 entre: minúscula, maiúscula, número, símbolo.
                  </p>
                  <CustomInputGroupText
                    name="novaSenha"
                    type="password"
                    placeholder="Nova senha"
                    addonType="prepend"
                    icon="tim-icons icon-lock-circle"
                  />
                  <CustomInputGroupText
                    name="confirmacao"
                    type="password"
                    placeholder="Confirme a nova senha"
                    addonType="prepend"
                    icon="tim-icons icon-lock-circle"
                  />
                </CardBody>
                <CardFooter>
                  <Button
                    block
                    className="btn-entrar mb-3"
                    size="lg"
                    type="submit"
                    disabled={submitting || !token}
                  >
                    {submitting ? "Salvando..." : "Redefinir senha"}
                  </Button>
                  <div style={{ textAlign: "center" }}>
                    <Link to="/auth/login" style={{ color: "#fff" }}>
                      Voltar ao login
                    </Link>
                  </div>
                </CardFooter>
              </Card>
            </Form>
          </Col>
        </Container>
      </div>
    </>
  );
};

export default ResetPassword;
